// ─────────────────────────────────────────────────────────────────────────────
// CollectionScene — gallery of all TCG cards the player has earned
// ─────────────────────────────────────────────────────────────────────────────

import Phaser from "phaser";
import { CARDS, ELEMENT_COLOR, RARITY_COLOR, RARITY_LABEL } from "../data/cardsData.js";
import { Collection } from "../game/collection.js";
import { createBackground } from "../vfx/background.js";
import { RegionThemes, hexToPhaser } from "../data/regionThemes.js";
import { I18n } from "../game/I18n.js";

import cardBackUrl    from "../assets/tcg/card_back.png";

const CARD_W   = 104;
const CARD_H   = 146;  // TCG ratio 750:1050
const COLS     = 4;
const GAP_X    = 10;
const GAP_Y    = 14;
const HEADER_H = 110;

// Detail modal — fill most of the 540x960 screen
const DETAIL_W = 460;
const DETAIL_H = 644;

export default class CollectionScene extends Phaser.Scene {
  constructor() { super("CollectionScene"); }

  preload() {
    if (!this.textures.exists("tcg-card-back"))
      this.load.image("tcg-card-back", cardBackUrl);

    // Preload all card art images
    for (const card of CARDS) {
      if (card.image) {
        const key = `tcg-art-${card.cardId}`;
        if (!this.textures.exists(key)) this.load.image(key, card.image);
      }
    }
  }

  init(data) {
    this.fromScene = data?.from || "MapScene";
  }

  create() {
    const W = this.scale.width;
    const H = this.scale.height;

    createBackground(this, "volcano");
    this.add.rectangle(W / 2, H / 2, W, H, 0x000000, 0.70).setDepth(0);

    // ── Header ──────────────────────────────────────────────────────────
    this.add.text(W / 2, 36, I18n.t('collection_title'), {
      fontFamily: "Kamikaze, system-ui", fontSize: "28px",
      color: "#e2e8f0", stroke: "#000000", strokeThickness: 4,
    }).setOrigin(0.5).setDepth(10);

    const owned     = new Set(Collection.getUniqueCardIds());
    const total     = CARDS.length;
    const ownedCount = owned.size;

    this.add.text(W / 2, 68, I18n.t('cards_count', { n: ownedCount, total }), {
      fontFamily: "system-ui", fontSize: "13px", color: "#60a5fa",
    }).setOrigin(0.5).setDepth(10);

    // Fragment progress bar
    const prog = Collection.getFragmentProgress();
    this.add.text(W / 2, 88, I18n.t('fragments_bar', { n: prog.current, max: prog.needed }), {
      fontFamily: "system-ui", fontSize: "11px", color: "#94a3b8",
    }).setOrigin(0.5).setDepth(10);

    // Back button
    const backBtn = this.add.text(28, 36, I18n.t('btn_map_back'), {
      fontFamily: "system-ui", fontSize: "13px", color: "#60a5fa",
    }).setOrigin(0, 0.5).setDepth(10).setInteractive({ useHandCursor: true });
    backBtn.on("pointerdown", () => this.scene.start(this.fromScene));
    backBtn.on("pointerover",  () => backBtn.setColor("#93c5fd"));
    backBtn.on("pointerout",   () => backBtn.setColor("#60a5fa"));

    // ── Scrollable card grid ─────────────────────────────────────────────
    const rowW   = COLS * CARD_W + (COLS - 1) * GAP_X;
    const startX = (W - rowW) / 2 + CARD_W / 2;
    const startY = HEADER_H + CARD_H / 2;

    const rows = Math.ceil(CARDS.length / COLS);
    const contentH = rows * (CARD_H + GAP_Y);

    // Mask for scrollable region
    const maskRect = this.add.rectangle(W / 2, HEADER_H + (H - HEADER_H) / 2, W, H - HEADER_H, 0x000000)
      .setAlpha(0.001).setDepth(1);
    const mask = maskRect.createGeometryMask();

    this._cardContainer = this.add.container(0, 0).setDepth(5).setMask(mask);

    CARDS.forEach((card, idx) => {
      const col  = idx % COLS;
      const row  = Math.floor(idx / COLS);
      const x    = startX + col * (CARD_W + GAP_X);
      const y    = startY + row * (CARD_H + GAP_Y);
      const isOwned = owned.has(card.id);
      this._buildMiniCard(x, y, card, isOwned);
    });

    // ── Scroll input (drag-based, distinguishes tap from drag) ──────────
    this._scrollY    = 0;
    this._maxScroll  = Math.max(0, contentH - (H - HEADER_H) + GAP_Y);
    this._dragStartY = null;
    this._dragDist   = 0;  // track total drag distance to distinguish tap vs scroll

    this.input.on("pointerdown", (p) => {
      if (this._popup) return; // don't scroll while popup is open
      this._dragStartY = p.y;
      this._dragDist = 0;
    });
    this.input.on("pointermove", (p) => {
      if (this._dragStartY === null || this._popup) return;
      const dy = this._dragStartY - p.y;
      this._dragStartY = p.y;
      this._dragDist += Math.abs(dy);
      this._scrollY = Phaser.Math.Clamp(this._scrollY + dy, 0, this._maxScroll);
      this._cardContainer.setY(-this._scrollY);
    });
    this.input.on("pointerup", () => { this._dragStartY = null; });

    // ── Detail popup ────────────────────────────────────────────────────
    this._popup = null;
  }

  _buildMiniCard(x, y, card, isOwned) {
    const c = this.add.container(x, y);
    this._cardContainer.add(c);

    const artKey = `tcg-art-${card.cardId}`;

    if (!isOwned) {
      // Locked — dark card back with "?"
      const back = this.textures.exists("tcg-card-back")
        ? this.add.image(0, 0, "tcg-card-back").setDisplaySize(CARD_W, CARD_H).setOrigin(0.5).setAlpha(0.3)
        : this.add.rectangle(0, 0, CARD_W, CARD_H, 0x111111);
      c.add(back);
      c.add(this.add.text(0, 0, "?", {
        fontFamily: "Kamikaze, system-ui", fontSize: "32px", color: "#475569",
      }).setOrigin(0.5));
    } else if (card.image && this.textures.exists(artKey)) {
      // Show real card art
      const art = this.add.image(0, 0, artKey)
        .setDisplaySize(CARD_W, CARD_H)
        .setOrigin(0.5);
      c.add(art);

      // Tap for detail popup (only if not dragging)
      c.setSize(CARD_W, CARD_H);
      c.setInteractive({ useHandCursor: true });
      c.on("pointerup", () => {
        if (this._dragDist < 10) this._showDetail(card);
      });
    } else {
      // Fallback: element-colored card
      const elColor = parseInt((ELEMENT_COLOR[card.element] || "#94a3b8").replace("#",""), 16);
      c.add(this.add.rectangle(0, 0, CARD_W, CARD_H, 0x1e293b).setStrokeStyle(2, elColor));
      c.add(this.add.text(0, 0, card.name, {
        fontFamily: "Kamikaze, system-ui", fontSize: "8px", color: "#e2e8f0",
        stroke: "#000000", strokeThickness: 1, align: "center",
        wordWrap: { width: CARD_W - 10 },
      }).setOrigin(0.5));
      c.setSize(CARD_W, CARD_H);
      c.setInteractive({ useHandCursor: true });
      c.on("pointerup", () => {
        if (this._dragDist < 10) this._showDetail(card);
      });
    }

    // Animate in
    c.setAlpha(0).setScale(0.9);
    this.tweens.add({ targets: c, alpha: isOwned ? 1 : 0.35, scaleX: 1, scaleY: 1,
      duration: 200, delay: Math.floor(Math.random() * 200), ease: "Quad.Out" });
  }

  _showDetail(card) {
    if (this._popup) { this._popup.destroy(); this._popup = null; return; }

    const W = this.scale.width;
    const H = this.scale.height;
    const popup = this.add.container(W / 2, H / 2).setDepth(50);
    this._popup = popup;

    const overlay = this.add.rectangle(0, 0, W, H, 0x000000, 0.82)
      .setInteractive();
    overlay.on("pointerdown", () => { popup.destroy(); this._popup = null; });
    popup.add(overlay);

    const artKey = `tcg-art-${card.cardId}`;

    if (card.image && this.textures.exists(artKey)) {
      // Full-screen card art
      const art = this.add.image(0, -20, artKey)
        .setDisplaySize(DETAIL_W, DETAIL_H)
        .setOrigin(0.5);
      popup.add(art);

      // Copies owned
      const copies = Collection.countCard(card.id);
      const copiesText = copies === 1 ? I18n.t('copies_one') : I18n.t('copies_many', { n: copies });
      popup.add(this.add.text(0, DETAIL_H / 2 - 4, copiesText, {
        fontFamily: "system-ui", fontSize: "12px", color: "#94a3b8",
      }).setOrigin(0.5));
    } else {
      // Fallback: text-based detail panel
      const panelW = 280, panelH = 340;
      const panel = this.add.rectangle(0, 0, panelW, panelH, 0x0f172a, 0.97)
        .setStrokeStyle(2, parseInt((RARITY_COLOR[card.rarity] || "#9ca3af").replace("#",""), 16));
      popup.add(panel);

      const top = -panelH / 2 + 20;
      popup.add(this.add.text(0, top + 40, card.name, {
        fontFamily: "Kamikaze, system-ui", fontSize: "22px",
        color: "#e2e8f0", stroke: "#000000", strokeThickness: 3,
      }).setOrigin(0.5));

      popup.add(this.add.text(0, top + 70,
        `${card.type.toUpperCase()} • ${card.element.toUpperCase()}  |  ${RARITY_LABEL[card.rarity]}`, {
        fontFamily: "system-ui", fontSize: "11px", color: RARITY_COLOR[card.rarity] || "#9ca3af",
      }).setOrigin(0.5));

      if (card.skill) {
        popup.add(this.add.text(0, top + 110, card.skill, {
          fontFamily: "system-ui", fontSize: "13px", color: "#f1f5f9", fontStyle: "bold",
        }).setOrigin(0.5));
      }
      popup.add(this.add.text(0, top + 135, card.skillText || "", {
        fontFamily: "system-ui", fontSize: "12px", color: "#94a3b8", align: "center",
        wordWrap: { width: panelW - 40 },
      }).setOrigin(0.5));
    }

    // Animate in
    popup.setScale(0.7).setAlpha(0);
    this.tweens.add({ targets: popup, scaleX: 1, scaleY: 1, alpha: 1, duration: 220, ease: "Back.Out" });
  }
}
